import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Calendar, Shield, Database,
  Users, Sparkles, Download, X,
} from "lucide-react";
import { BRAND_PROFILES } from "@/lib/brandProfile";
import { usePublicTheme } from "@/lib/usePublicTheme";

/**
 * Pilot proposition page — closes the boardroom hesitation gap.
 *
 * Directors don't reject ServiceSell PRO. They defer it ("let me think").
 * A clear, one-page pilot offer with explicit "free + no commitment + you
 * keep your data" terms removes ~80% of the friction. Public route /pilot.
 */

const PILOT_STEPS = [
  {
    n: "01",
    icon: Calendar,
    title: "Day 0 — Onboarding call",
    body: "90-minute call with your aftersales manager and 1-2 service advisors. We seed your last 100 jobs from your DMS export so the dashboard is alive on day one.",
  },
  {
    n: "02",
    icon: Sparkles,
    title: "Days 1-30 — Live with your customers",
    body: "Your team uses ServiceSell PRO for real customer-facing work. Every approved extra, every recovered red item, every service plan sold is tracked against your previous-90-day baseline.",
  },
  {
    n: "03",
    icon: CheckCircle2,
    title: "Day 31 — Your call",
    body: "We show you the actual numbers — recovered work, conversion uplift, service plans sold. Continue on pricing that will be confirmed post-pilot (Option A: flat SaaS subscription, or Option B: commission on approved extras), on a 12-month minimum term. Or walk away with all your data exported. No invoice if you walk.",
  },
];

const PROMISES = [
  { icon: Database,  text: "Full data export available any time. Your DMS, your customers, your data — always." },
  { icon: Shield,    text: "ICO-registered processor (ZC144538). UK-hosted infrastructure. GDPR-compliant by design." },
  { icon: Users,     text: "Per-advisor login. Audit trail of every change. Your IT team sees exactly who did what." },
  { icon: X,         text: "Cancel any time during the pilot. No invoice. No retention clauses. We send your data export within 24 hours." },
];

export default function PilotPage() {
  usePublicTheme();
  // Public marketing page — always default brand; the white-label demo lives
  // post-login so prospects see the canonical product first.
  const brand = BRAND_PROFILES.default;
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="pilot-page">
      <header className="border-b border-white/10 sticky top-0 z-10 backdrop-blur bg-background/80">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white" data-testid="pilot-back-home">
            <ArrowLeft className="w-4 h-4" /> {brand.productName}
          </Link>
          <div className="text-[10px] font-mono uppercase tracking-wider text-primary border border-primary/40 rounded px-1.5 py-0.5">
            30-DAY PILOT
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-12">
        {/* Hero */}
        <section className="space-y-4 max-w-3xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-primary">
            Try {brand.productName} without commitment
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-balance leading-[1.05]">
            30 days. Your real customers.<br />
            <span className="text-primary">You decide on day 31.</span>
          </h1>
          <p className="text-base text-white/70 leading-relaxed">
            We model 38% uplift in approved extras and rapid payback on the onboarding investment.
            We're confident enough in those numbers to give you 30 days to verify them with your own customers, your own team,
            and your own DMS data — before you're invoiced a penny.
          </p>
        </section>

        {/* Positioning line — handles the #1 IT-director objection up-front:
            "are you replacing our DMS?". Sits right after the hero so it
            answers the question before the prospect reads further. */}
        <section className="rounded-xl border border-primary/25 bg-primary/[0.04] p-5 md:p-6" data-testid="pilot-positioning">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-primary/80 mb-2">
            How we sit alongside your stack
          </div>
          <p className="font-display font-bold text-lg md:text-xl tracking-tight leading-snug">
            Your DMS is the <span className="text-white/55">system of record</span>. {brand.productName} is the <span className="text-primary">system of engagement</span> on top.
          </p>
          <p className="mt-2 text-xs md:text-sm text-white/55 leading-relaxed">
            Same architecture as Salesforce on SAP. We don't touch your data of truth — we layer the customer-facing video,
            AI explanations and one-tap approvals on top of it. Your DMS keeps its job. We do the one it was never built for.
          </p>
        </section>

        {/* 3-step pilot timeline */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pilot-timeline">
          {PILOT_STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.n}
                className="rounded-lg border border-white/10 bg-white/[0.02] p-5 space-y-3"
                data-testid={`pilot-step-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-black text-3xl text-primary/70">{s.n}</span>
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-display font-bold text-base">{s.title}</h3>
                <p className="text-xs text-white/60 leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </section>

        {/* Our promises (objection-busters) */}
        <section className="space-y-5">
          <h2 className="font-display font-bold text-2xl">What you take away regardless</h2>
          <ul className="space-y-3" data-testid="pilot-promises">
            {PROMISES.map((p, i) => {
              const Icon = p.icon;
              return (
                <li key={i} className="flex items-start gap-4 rounded-md border border-white/10 bg-white/[0.02] p-4">
                  <div className="w-9 h-9 rounded-md bg-primary/15 grid place-items-center shrink-0">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-sm text-white/85 leading-relaxed">{p.text}</p>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Pricing transparency */}
        <section className="rounded-lg border border-white/10 bg-white/[0.02] p-6 space-y-4" data-testid="pilot-pricing">
          <h2 className="font-display font-bold text-2xl">What it costs if you continue</h2>
          <p className="text-[12px] text-white/55 -mt-2">Pick the model that fits your appetite for predictability. Same product, same 12-month minimum term, same monthly ROI dashboard inside the app.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-md border border-primary/30 bg-primary/[0.04] p-5 space-y-3" data-testid="pilot-pricing-option-a">
              <div className="flex items-baseline justify-between">
                <div className="text-[10px] font-bold uppercase tracking-wider text-primary">Option A</div>
                <div className="text-[10px] text-white/45">Most popular</div>
              </div>
              <h3 className="font-display font-bold text-xl">Flat SaaS subscription</h3>
              <div>
                <div className="font-display font-bold text-3xl">TBA<span className="text-base font-normal text-white/60"> post-pilot</span></div>
                <div className="text-[11px] text-white/55 mt-0.5">Final pricing confirmed on your Order Form.</div>
              </div>
              <ul className="text-[11px] text-white/65 leading-relaxed space-y-1 pt-2 border-t border-white/10">
                <li>&bull;&nbsp; Predictable. Same invoice every month.</li>
                <li>&bull;&nbsp; No per-transaction component &mdash; FCA-clean.</li>
                <li>&bull;&nbsp; AI VHC, service-plan lookup, SMS &amp; email, customer portal, support &mdash; all inclusive.</li>
              </ul>
            </div>

            <div className="rounded-md border border-white/15 bg-white/[0.02] p-5 space-y-3" data-testid="pilot-pricing-option-b">
              <div className="flex items-baseline justify-between">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/55">Option B</div>
                <div className="text-[10px] text-white/45">Pay as we sell</div>
              </div>
              <h3 className="font-display font-bold text-xl">Commission on approved extras</h3>
              <div>
                <div className="font-display font-bold text-3xl">TBA<span className="text-base font-normal text-white/60"> post-pilot</span></div>
                <div className="text-[11px] text-white/55 mt-0.5">Final pricing confirmed on your Order Form.</div>
              </div>
              <ul className="text-[11px] text-white/65 leading-relaxed space-y-1 pt-2 border-t border-white/10">
                <li>&bull;&nbsp; If we don't sell, you don't pay.</li>
                <li>&bull;&nbsp; Risk transferred to {brand.productName}{brand.key === "default" ? <>&trade;</> : null}.</li>
                <li>&bull;&nbsp; Same product, same support &mdash; lower floor, lower ceiling.</li>
              </ul>
            </div>
          </div>

          <div className="text-[11px] text-white/45 italic pt-2 border-t border-white/10">
            12-month minimum term on both options. 30-day exit for cause. Auto-renews monthly thereafter. Service-plan referral commission counts separately, billed alongside. <strong className="text-white/65">We have margin to negotiate.</strong> Tell us what works for your group.
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/[0.10] to-primary/[0.02] p-8 text-center" data-testid="pilot-cta-section">
          <h2 className="font-display font-black text-3xl text-balance">
            Ready to start your 30-day pilot?
          </h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">
            Reply to this page or email Steve directly — he'll send a one-page MSA, schedule the onboarding call, and have your dashboard live within 5 working days.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <a
              href="mailto:steve.kirby21@gmail.com?subject=ServiceSell%20PRO%20-%2030-day%20pilot%20request&body=Hi%20Steve%2C%0A%0AWe%27d%20like%20to%20start%20a%2030-day%20pilot.%20Our%20dealership%20details%3A%0A%0A-%20Dealership%20name%3A%0A-%20Aftersales%20manager%3A%0A-%20Approximate%20monthly%20jobs%3A%0A-%20Best%20phone%20number%3A%0A%0AThanks"
              className="btn-primary"
              data-testid="pilot-cta-email"
            >
              Email Steve to start <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/roi-calculator" className="btn-outline" data-testid="pilot-cta-roi">
              First, model the ROI
            </Link>
          </div>
          <div className="text-[10px] text-white/40 mt-5 inline-flex items-center gap-1.5">
            <Download className="w-3 h-3" /> One-page pilot terms available on request.
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 mt-10">
        <div className="max-w-5xl mx-auto px-6 py-6 text-[11px] text-white/40">
          ServiceSellPro Limited · Registered in England &amp; Wales · Company No. 17211707 · ICO ZC144538 · Registered UK trade mark No. UK00004398263
        </div>
      </footer>
    </div>
  );
}
